/**
 * PromptList — scrollable list of prompt families, grouped.
 *
 * @tanstack/react-virtual is NOT installed; uses a plain scrollable div with
 * max-height. Step 10 will virtualize when the dependency is added.
 *
 * @see B-popup-frontend.md step 4, step 10
 */
import { useState } from 'react';
import { Box } from '@mui/material';
import type { BatchOp, Family, ListGroupKey, PromptAllowanceFigures } from './promptStudioModel';
import { AXIS_COLOR, lastRunOf, needRowsReason, runBatch } from './promptStudioModel';
import { PromptRow } from './PromptRow';
import { PausedRow } from './PausedRow';
import type { BrandQueryAxis, BrandStrategy, FirstRunNotice, PromptLastRun, Location } from '@traaker/shared';
import { brandQueriesApi } from '../../services/brand-queries';

interface GroupItem {
  key: ListGroupKey;
  families: Family[];
  /** Grouped by term / place: the header text and (terms) the axis dot. */
  label?: string;
  axis?: BrandQueryAxis;
}

export interface PromptListProps {
  groups: GroupItem[];
  openId: string | null;
  q?: string;
  runs: Record<string, PromptLastRun> | null;
  locations?: Location[];
  /** brandId passed through to PromptRow / PausedRow for API calls (step 11). */
  brandId?: string;
  /** Place search language (brand working language), passed to PromptRow. */
  lang?: string;
  /** The brand's vocabularies, for the opened prompt's theme / criteria chips. */
  strategy?: BrandStrategy;
  /** The Lieu filter, for each row's « first place +N » cell. */
  zoneFilter?: string[];
  /** Tier — needed for agency preview flow in PausedRow. */
  tier?: string;
  /** Allowance figures for greying paused rows before any click (needRowsReason). */
  promptFigures?: PromptAllowanceFigures | null;
  onOpen: (id: string | null) => void;
  onTag?: (tag: string) => void;
  onDisable?: (family: Family) => void;
  onDelete?: (family: Family) => void;
  onRetryClassify?: (queryId: string) => void;
  onReactivate?: (family: Family) => void;
  /** Called after any in-row action completes (Désactiver, Réactiver, Supprimer, save). */
  onChanged?: () => void;
  /** A creating save in an opened prompt (lot D) → the first-run strip. */
  onFirstRun?: (firstRun?: FirstRunNotice[]) => void;
  hasFilter?: boolean;
  onClearFilter?: () => void;
  onAdd?: () => void;
  /** Batch selection (family root ids); an active row shows its checkbox when `onSelect` is set. */
  selected?: ReadonlySet<string>;
  onSelect?: (rootId: string) => void;
}

const GROUP_LABEL: Partial<Record<ListGroupKey, string>> = {
  pending: 'En attente', // i18n: promptStudio.group.pending
  narrative: 'Image',   // i18n: promptStudio.axis.narrative
  authority: 'Autorité', // i18n: promptStudio.axis.authority
  referral: 'Recommandation', // i18n: promptStudio.axis.referral
  comparison: 'Comparaison', // i18n: promptStudio.axis.comparison
  disabled: 'Désactivés', // i18n: promptStudio.group.disabled
  paused: 'Mis en pause (offre)', // i18n: promptStudio.group.paused
};

export function PromptList({
  groups,
  openId,
  q = '',
  runs,
  locations = [],
  brandId,
  lang,
  strategy,
  zoneFilter,
  tier,
  promptFigures,
  onOpen,
  onTag,
  onDisable,
  onDelete,
  onRetryClassify,
  onReactivate: _onReactivate,
  onChanged,
  onFirstRun,
  hasFilter = false,
  onClearFilter,
  onAdd,
  selected,
  onSelect,
}: PromptListProps) {
  const totalFamilies = groups.reduce((acc, g) => acc + g.families.length, 0);

  if (totalFamilies === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 1,
          color: 'text.secondary',
          fontSize: 13,
          py: 6,
        }}
      >
        {hasFilter ? (
          <>
            {/* i18n: promptStudio.list.noResults */}
            Aucun résultat
            <Box
              component="button"
              onClick={onClearFilter}
              sx={{ fontSize: 13, color: '#7c3aed', border: 'none', bgcolor: 'transparent', cursor: 'pointer', textDecoration: 'underline' }}
            >
              {/* i18n: promptStudio.list.clearFilter */}
              Tout effacer
            </Box>
          </>
        ) : (
          <Box
            component="button"
            onClick={onAdd}
            sx={{ fontSize: 13, color: '#7c3aed', border: 'none', bgcolor: 'transparent', cursor: 'pointer' }}
          >
            {/* i18n: promptStudio.list.empty */}
            + Ajouter des prompts
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflowY: 'auto' }}>
      {groups.map((group) => (
        <Box key={group.key}>
          {/* Sticky group header */}
          <GroupHeader groupKey={group.key} count={group.families.length} label={group.label} axis={group.axis} />

          {/* Rows */}
          {(group.key === 'disabled' || group.key === 'paused') ? (
            group.families.map((f) => (
              <PausedRow
                key={f.root.queryId}
                family={f}
                variant={group.key === 'paused' ? 'plan' : 'user'}
                brandId={brandId ?? ''}
                runs={runs}
                tier={tier}
                preGreyed={needRowsReason(f.rows.length, promptFigures ?? null, f.root.axis as BrandQueryAxis)}
                onChanged={onChanged}
              />
            ))
          ) : (
            group.families.map((f) => (
              <PromptRow
                key={f.root.queryId}
                family={f}
                open={openId === f.root.queryId}
                q={q}
                lastRun={lastRunOf(f, runs)}
                locations={locations}
                onOpen={onOpen}
                onTag={onTag}
                onDisable={onDisable}
                onDelete={onDelete}
                onRetryClassify={onRetryClassify}
                runs={runs}
                brandId={brandId}
                lang={lang}
                strategy={strategy}
                zoneFilter={zoneFilter}
                onChanged={onChanged}
                promptFigures={promptFigures}
                isAgency={tier === 'agency'}
                onFirstRun={onFirstRun}
                selected={selected?.has(f.root.queryId)}
                onSelect={f.root.status === 'active' ? onSelect : undefined}
              />
            ))
          )}
        </Box>
      ))}
    </Box>
  );
}

interface GroupHeaderProps {
  groupKey: ListGroupKey;
  count: number;
  label?: string;
  axis?: BrandQueryAxis;
}

function GroupHeader({ groupKey, count, label: groupLabel, axis }: GroupHeaderProps) {
  const label = groupLabel ?? GROUP_LABEL[groupKey] ?? groupKey;
  const dotColor = axis
    ? AXIS_COLOR[axis]
    : groupKey !== 'pending' && groupKey !== 'disabled' && groupKey !== 'paused'
      ? AXIS_COLOR[groupKey as keyof typeof AXIS_COLOR]
      : undefined;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 0.75,
        bgcolor: '#f8f8fc',
        borderBottom: '1px solid',
        borderColor: 'rgba(0,0,0,.07)',
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.04em',
        color: 'text.secondary',
      }}
    >
      {groupKey === 'pending' ? (
        /* Hollow amber ring for pending */
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            border: '1.5px solid #f59e0b',
            flexShrink: 0,
          }}
        />
      ) : dotColor ? (
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: dotColor,
            flexShrink: 0,
          }}
        />
      ) : null}
      {label}
      <Box sx={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>({count})</Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// BatchBar — the selected rows' Désactiver / Supprimer (owner request)
// ---------------------------------------------------------------------------

export interface BatchBarProps {
  /** The selected families still listed and active. */
  families: Family[];
  brandId: string;
  /** The batch finished: `failed` = the roots that could not be processed (they stay selected). */
  onDone: (failed: string[]) => void;
  onClear: () => void;
}

const BAR_BTN = { fontSize: 12, border: 'none', bgcolor: 'transparent', p: 0, cursor: 'pointer', '&:disabled': { cursor: 'default', opacity: 0.5 } } as const;

export function BatchBar({ families, brandId, onDone, onClear }: BatchBarProps) {
  const [run, setRun] = useState<{ op: BatchOp; done: number; total: number } | null>(null);
  const [failed, setFailed] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const n = families.length;
  // The opened prompt offers no Désactiver on a pending row; neither does the batch.
  const hasPending = families.some((f) => f.root.axis === 'pending');
  const progress = run ? ` ${run.done}/${run.total}` : '';

  async function start(op: BatchOp) {
    if (run) return;
    const batch = [...families];
    setFailed(0);
    setRun({ op, done: 0, total: batch.length });
    const failedIds = await runBatch(
      batch,
      op,
      (c) => brandQueriesApi.archive(brandId, c.axis, c.queryId, c.reason),
      (done) => setRun({ op, done, total: batch.length }),
    );
    setRun(null);
    setConfirmOpen(false);
    setFailed(failedIds.length);
    onDone(failedIds);
  }

  return (
    <Box sx={{ px: 2, py: 0.5, flexShrink: 0, fontSize: 12, bgcolor: 'rgba(124,58,237,.05)', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
        {/* i18n: promptStudio.batch.selected */}
        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{n} sélectionné{n > 1 ? 's' : ''}</Box>
        ·
        <Box
          component="button"
          onClick={() => void start('disable')}
          disabled={!!run || hasPending}
          // i18n: promptStudio.batch.disablePending
          title={hasPending ? 'Les prompts en attente de classement ne se désactivent pas' : undefined}
          sx={{ ...BAR_BTN, color: 'text.secondary' }}
        >
          {/* i18n: promptStudio.row.disable / promptStudio.batch.disabling */}
          {run?.op === 'disable' ? `Désactivation…${progress}` : 'Désactiver'}
        </Box>
        ·
        <Box component="button" onClick={() => setConfirmOpen(true)} disabled={!!run} sx={{ ...BAR_BTN, color: 'error.main' }}>
          {/* i18n: promptStudio.row.delete */}
          Supprimer
        </Box>
        ·
        <Box component="button" onClick={onClear} disabled={!!run} sx={{ ...BAR_BTN, color: 'text.secondary', textDecoration: 'underline' }}>
          {/* i18n: promptStudio.batch.clear */}
          Tout désélectionner
        </Box>
      </Box>
      {failed > 0 && (
        <Box sx={{ color: '#b45309', mt: 0.25 }}>
          {/* i18n: promptStudio.batch.failed_one / _other */}
          {failed > 1 ? `${failed} n'ont pas pu être traités, réessayez.` : "1 n'a pas pu être traité, réessayez."}
        </Box>
      )}
      {confirmOpen && (
        <BatchDeleteDialog
          n={n}
          running={run?.op === 'delete' ? progress : null}
          onConfirm={() => void start('delete')}
          onCancel={() => { if (!run) setConfirmOpen(false); }}
        />
      )}
    </Box>
  );
}

/** Same frame and copy as PromptRowExpanded's DeleteConfirmDialog (UI-SPEC §2.6), for n questions. */
function BatchDeleteDialog({ n, running, onConfirm, onCancel }: {
  n: number;
  /** null = idle; else the progress suffix (« 2/5 »). */
  running: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // i18n: promptStudio.batch.deleteBody_one / _other
  const bodyText = n > 1
    ? `Ces ${n} questions et tous leurs prompts (langues et lieux) ne seront plus mesurés et disparaissent de la liste. Leurs résultats passés restent consultables.`
    : 'Cette question et tous ses prompts (langues et lieux) ne seront plus mesurés et disparaissent de la liste. Leurs résultats passés restent consultables.';
  const loading = running !== null;

  return (
    <Box
      sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(11,11,15,.3)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onCancel}
    >
      <Box
        sx={{ width: 440, bgcolor: 'background.paper', borderRadius: 3, p: 3, boxShadow: '0 24px 70px rgba(0,0,0,.22)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>
          {/* i18n: promptStudio.batch.deleteTitle_one / _other */}
          {n > 1 ? 'Supprimer ces prompts ?' : 'Supprimer ce prompt ?'}
        </Box>
        <Box sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1 }}>
          {bodyText}
        </Box>
        <Box sx={{ fontSize: 12.5, color: 'text.disabled', mb: 2 }}>
          {/* i18n: promptStudio.delete.hint */}
          Pour arrêter la mesure sans supprimer, utilisez Désactiver.
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Box
            component="button"
            onClick={onCancel}
            disabled={loading}
            sx={{ fontSize: 13, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 2, py: 0.75, bgcolor: 'transparent', cursor: 'pointer' }}
          >
            {/* i18n: common.cancel */}
            Annuler
          </Box>
          <Box
            component="button"
            onClick={onConfirm}
            disabled={loading}
            sx={{ fontSize: 13, border: 'none', borderRadius: 1, px: 2, py: 0.75, bgcolor: 'error.main', color: '#fff', cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {/* i18n: common.delete / promptStudio.batch.deleting */}
            {loading ? `Suppression…${running}` : 'Supprimer'}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
